import { useState } from 'react';
import { Clock, Calendar, User, PlayCircle, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export const AppointmentCard = ({
  appointment,
  onViewDetails,
  onStatusChange,
  showActions = true
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const statusConfig = {
    scheduled: {
      className: 'bg-blue-100 text-blue-800',
      label: 'Scheduled'
    },
    'in-progress': {
      className: 'bg-yellow-100 text-yellow-800',
      label: 'In Progress'
    },
    completed: {
      className: 'bg-green-100 text-green-800',
      label: 'Completed'
    },
    cancelled: {
      className: 'bg-red-100 text-red-800',
      label: 'Cancelled'
    },
    'no-show': {
      className: 'bg-gray-100 text-gray-800',
      label: 'No Show'
    }
  };

  // Fix for AppointmentCard.jsx
  // src/components/appointments/AppointmentCard.jsx
  const handleStartSession = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);
    try {
      // Update appointment status
      if (onStatusChange) {
        await onStatusChange('in-progress');
      }

      // Check if this is a virtual session
      if (appointment.sessionType === 'virtual') {
        // For virtual sessions, navigate to video interface
        navigate(`/${user.role}/virtual-session/${appointment._id}`);
      } else {
        // For physical sessions, navigate to form-based session
        const sessionPath = `/counselor/sessions/${appointment._id}`;
        navigate(sessionPath);
      }
    } catch (error) {
      console.error('Failed to start session:', error);
      setIsLoading(false); // Make sure we set loading to false on error
    }
  };
  const getStatusBadge = () => {
    const config = statusConfig[appointment.status] || statusConfig.scheduled;
    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const canStartSession =
    (user?.role === "counselor") &&
    appointment.status === 'scheduled' &&
    appointment.counselor?._id === user?.id;

  const isUpcoming = new Date(appointment.date) >= new Date();

  return (
    <div
      onClick={() => onViewDetails(appointment._id)}
      className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200 overflow-hidden"
    >
      {/* Header with status */}
      <div className="px-4 py-3 border-b flex justify-between items-center">
        {getStatusBadge()}
        {appointment.isUrgent && (
          <div className="flex items-center text-red-600">
            <AlertCircle className="h-4 w-4 mr-1" />
            <span className="text-xs font-medium">Urgent</span>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="p-4">
        <div className="space-y-3">
          {/* Client/Counselor Info */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-gray-900">
                {user?.role === 'counselor'
                  ? appointment.client?.fullName
                  : appointment.counselor?.fullName}
              </h3>
              <div className="flex items-center text-sm text-gray-500">
                <User className="h-4 w-4 mr-1" />
                {user?.role === 'counselor' ? 'Client' : 'Counselor'}
              </div>
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {new Date(appointment.date).toLocaleDateString()}
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {appointment.timeSlot}
            </div>
          </div>

          {/* Service Type */}
          <div className="text-sm text-gray-600">
            {appointment.serviceType.split('-').map(word =>
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')}
          </div>
          <div className="text-sm text-gray-600">
            {appointment.sessionType?.charAt(0).toUpperCase() + appointment.sessionType?.slice(1)}
          </div>

        </div>

        {/* Actions */}
        {showActions && (
          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(appointment._id);
              }}
              className="px-3 py-1.5 text-sm text-uzima-maroon-500 hover:bg-uzima-maroon-50 rounded transition-colors duration-200"
            >
              View Details
            </button>

            {canStartSession && isUpcoming && (
              <button
                onClick={handleStartSession}
                disabled={isLoading}
                className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors duration-200 disabled:bg-green-300"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Starting...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-1" />
                    Start Session
                  </>
                )}
              </button>
            )}

            {/* {appointment.status === 'scheduled' && isUpcoming && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange('cancelled');
                }}
                className="flex items-center px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition-colors duration-200"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Cancel
              </button>
            )} */}

            {appointment.status === 'in-progress' && user?.role === 'counselor' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange('completed');
                }}
                className="flex items-center px-3 py-1.5 text-sm bg-uzima-maroon-500 text-white rounded hover:bg-uzima-maroon-600 transition-colors duration-200"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Complete Session
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentCard;